import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import Avatar from 'components/Avatar'
import GroupsIcon from '@mui/icons-material/Groups'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditIcon from '@mui/icons-material/Edit'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import { maskEmail } from 'utils/string'
import {
  updateTeamRoutine,
  deleteTeamRoutine,
  addTeamMemberRoutine,
  removeTeamMemberRoutine
} from '../ducks/actions'
import { selectTeams } from '../ducks/selectors'
import UserPicker from './UserPicker'

const TeamManagement = ({ teamId, onClose }) => {
  const dispatch = useDispatch()
  const teams = useSelector(selectTeams)
  const team = teams.find(t => String(t.id) === String(teamId))

  const [isEditingName, setIsEditingName] = useState(false)
  const [editName, setEditName] = useState('')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showEmailInvite, setShowEmailInvite] = useState(false)
  const [emailInput, setEmailInput] = useState('')
  const [nameInput, setNameInput] = useState('')

  useEffect(() => {
    if (team) setEditName(team.name)
  }, [team])

  if (!team) return null

  const existingMemberIds = team.members?.filter(m => m.user).map(m => m.user.id) || []

  const handleSaveName = () => {
    if (editName.trim() && editName.trim() !== team.name) {
      dispatch(updateTeamRoutine({ id: team.id, name: editName.trim() }))
    }
    setIsEditingName(false)
  }

  const handleUserPickerChange = (userIds) => {
    const newIds = userIds.filter(id => !existingMemberIds.includes(id))
    newIds.forEach(userId => {
      dispatch(addTeamMemberRoutine({ teamId: team.id, userId }))
    })
  }

  const handleAddByEmail = () => {
    if (emailInput.trim()) {
      dispatch(addTeamMemberRoutine({ teamId: team.id, email: emailInput.trim(), ...(nameInput.trim() ? { name: nameInput.trim() } : {}) }))
      setEmailInput('')
      setNameInput('')
    }
  }

  const handleRemoveMember = (memberId) => {
    dispatch(removeTeamMemberRoutine({ teamId: team.id, memberId }))
  }

  const handleDeleteTeam = () => {
    dispatch(deleteTeamRoutine(team.id))
    onClose()
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <GroupsIcon />
          {isEditingName && !team.isSystem ? (
            <NameEditRow>
              <NameInput
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveName()
                  if (e.key === 'Escape') setIsEditingName(false)
                }}
              />
              <SmallIconBtn onClick={handleSaveName}><CheckIcon /></SmallIconBtn>
              <SmallIconBtn onClick={() => setIsEditingName(false)}><CloseIcon /></SmallIconBtn>
            </NameEditRow>
          ) : (
            <TeamName>
              {team.name}
              {!team.isSystem && <SmallIconBtn onClick={() => setIsEditingName(true)}><EditIcon /></SmallIconBtn>}
            </TeamName>
          )}
        </HeaderLeft>
        {!team.isSystem && (
          <DeleteBtn onClick={() => setShowDeleteConfirm(true)}>
            <DeleteOutlineIcon /> Delete Team
          </DeleteBtn>
        )}
      </Header>

      {team.isSystem && (
        <SystemNote>
          This team is automatically managed. Members are synced daily based on the <strong>@{team.domain}</strong> email domain.
        </SystemNote>
      )}

      <Section>
        <SectionTitle>Members ({team.members?.length || 0})</SectionTitle>
        {!team.isSystem && <AddMemberSection>
          <UserPicker
            value={[]}
            onChange={handleUserPickerChange}
            excludeIds={existingMemberIds}
            placeholder='Search users to add...'
          />
          <EmailToggle onClick={() => setShowEmailInvite(!showEmailInvite)}>
            <MailOutlineIcon style={{ fontSize: 16 }} />
            {showEmailInvite ? 'Hide email invite' : 'Invite by email instead'}
          </EmailToggle>
          {showEmailInvite && (
            <AddMemberRow>
              <MemberInput
                type='text'
                placeholder='Name (optional)'
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                style={{ maxWidth: 160 }}
              />
              <MemberInput
                type='email'
                placeholder='user@example.com'
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddByEmail()}
              />
              <AddBtn onClick={handleAddByEmail} disabled={!emailInput.trim()}>
                <PersonAddIcon /> Invite
              </AddBtn>
            </AddMemberRow>
          )}
        </AddMemberSection>}

        <MemberList>
          {team.members?.map(member => (
            <MemberRow key={member.id}>
              <Avatar
                firstName={member.user?.firstName || member.name}
                lastName={member.user?.lastName}
                email={member.user?.email || member.email}
                src={member.user?.avatar}
                size={36}
              />
              <MemberInfo>
                <MemberName>
                  {member.user
                    ? `${member.user.firstName || ''} ${member.user.lastName || ''}`.trim() || member.name || maskEmail(member.email)
                    : member.name || maskEmail(member.email)}
                </MemberName>
                <MemberMeta>
                  {member.role === 'manager' && <RoleBadge>Manager</RoleBadge>}
                  {member.isPending && <ExternalBadge>External</ExternalBadge>}
                  <span>{maskEmail(member.email)}</span>
                </MemberMeta>
              </MemberInfo>
              {!team.isSystem && member.role !== 'manager' && (
                <RemoveBtn onClick={() => handleRemoveMember(member.id)}>
                  <CloseIcon />
                </RemoveBtn>
              )}
            </MemberRow>
          ))}
        </MemberList>
      </Section>

      {showDeleteConfirm && (
        <DeleteOverlay onClick={() => setShowDeleteConfirm(false)}>
          <DeleteDialog onClick={(e) => e.stopPropagation()}>
            <DeleteDialogTitle>Delete Team</DeleteDialogTitle>
            <DeleteDialogText>
              Are you sure you want to delete "{team.name}"? This action cannot be undone.
            </DeleteDialogText>
            <DeleteDialogActions>
              <CancelBtn onClick={() => setShowDeleteConfirm(false)}>Cancel</CancelBtn>
              <ConfirmDeleteBtn onClick={handleDeleteTeam}>Delete</ConfirmDeleteBtn>
            </DeleteDialogActions>
          </DeleteDialog>
        </DeleteOverlay>
      )}
    </Container>
  )
}

export default TeamManagement

const Container = styled.div`
  padding: 24px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 12px;
  }
`

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  > svg {
    font-size: 28px;
    color: ${({ theme }) => theme.colors.primary};
  }
`

const TeamName = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
`

const NameEditRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`

const NameInput = styled.input`
  font-size: 18px;
  font-weight: 600;
  padding: 4px 8px;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: 6px;
  outline: none;
  color: ${({ theme }) => theme.colors.haiti};
`

const SmallIconBtn = styled.button`
  width: 28px;
  height: 28px;
  border: none;
  background: ${({ theme }) => theme.colors.lightGrey};
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  svg { font-size: 16px; }

  &:hover {
    background: ${({ theme }) => theme.colors.mediumGrey};
  }

  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
  }
`

const DeleteBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: white;
  color: ${({ theme }) => theme.colors.error || '#d14925'};
  border: 1px solid ${({ theme }) => theme.colors.error || '#d14925'};
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;

  svg { font-size: 18px; }

  &:hover {
    background: #fff1f0;
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    padding: 10px 14px;
  }
`

const Section = styled.div``

const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  margin-bottom: 12px;
`

const AddMemberSection = styled.div`
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const EmailToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  cursor: pointer;
  padding: 0;
  align-self: flex-start;

  &:hover {
    color: ${({ theme }) => theme.colors.secondary};
  }
`

const AddMemberRow = styled.div`
  display: flex;
  gap: 8px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`

const MemberInput = styled.input`
  flex: 1;
  padding: 10px 14px;
  font-size: 14px;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 8px;
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(210, 71, 36, 0.1);
  }
`

const AddBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: ${({ theme }) => theme.colors.secondary};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  svg { font-size: 18px; }

  &:hover:not(:disabled) { opacity: 0.9; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`

const MemberList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const MemberRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  transition: background 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.lightGrey};
  }
`

const MemberInfo = styled.div`
  flex: 1;
  min-width: 0;
`

const MemberName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.haiti};
`

const MemberMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  margin-top: 2px;
`

const RoleBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 4px;
  color: #7c3aed;
  background: #f5f3ff;
`

const SystemNote = styled.div`
  font-size: 13px;
  color: #475569;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 20px;
  line-height: 1.5;
`

const ExternalBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 4px;
  color: #0d7377;
  background: #e0f7f7;
`

const RemoveBtn = styled.button`
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};

  svg { font-size: 18px; }

  &:hover {
    background: #fee2e2;
    color: ${({ theme }) => theme.colors.error || '#d14925'};
  }

  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
  }
`

const DeleteOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`

const DeleteDialog = styled.div`
  background: white;
  border-radius: 12px;
  padding: 28px;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
`

const DeleteDialogTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  margin: 0 0 8px 0;
`

const DeleteDialogText = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  margin: 0 0 20px 0;
`

const DeleteDialogActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`

const CancelBtn = styled.button`
  padding: 10px 20px;
  background: white;
  color: ${({ theme }) => theme.colors.haiti};
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &:hover { background: ${({ theme }) => theme.colors.lightGrey}; }
`

const ConfirmDeleteBtn = styled.button`
  padding: 10px 20px;
  background: ${({ theme }) => theme.colors.error || '#d14925'};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:hover { opacity: 0.9; }
`
